// Component: DsMobileShowcase.jsx
// Purpose: Dev-only, phone-first showcase for the DeeTrade Design System's
//          MOBILE primitive layer (Sheet, TabBar, MobileHeader, ListRow,
//          SwipeRow, StatTile/NumberHero, Sparkline). Reachable at /ds/mobile
//          in dev only — mirrors the /ds (DsShowcase) route-gating exactly.
//
//          Unlike /ds (which renders light+dark side-by-side on a desktop
//          canvas), this page is single-column and phone-width: content is
//          capped at 430px and centered, the page background fills wider
//          viewports, and a Segmented toggle flips [data-theme] on the
//          wrapper instead of rendering two panels (side-by-side doesn't fit
//          375px). Verified with no horizontal overflow at 375px wide.

import React, { useState } from "react";
import {
  Button,
  Segmented,
  Toggle,
  Icon,
  Sheet,
  TabBar,
  MobileHeader,
  ListRow,
  SwipeRow,
  StatTile,
  NumberHero,
  Sparkline,
  RollingNumber,
  TickFlash,
  PullToRefresh,
  haptics,
  hapticsEnabled,
  setHapticsEnabled,
} from "../../components/ds";

/*>>> SAMPLE DATA — realistic ₹ figures / NSE symbols, same shape as
     MobileDashboard.jsx / MobileWatchList.jsx <<<*/
const SPARK_PROFIT = [6, 10, 8, 14, 12, 18, 16, 20, 18, 24, 22];
const SPARK_LOSS = [22, 20, 21, 18, 16, 17, 13, 11, 9, 7, 6];
const SPARK_FLAT = [12, 13, 11, 12, 14, 12, 13, 12, 11, 13, 12];

const SESSION_STATS = [
  { k: "Realized", v: "₹8,420" },
  { k: "Unrealized", v: "₹4,427" },
  { k: "Orders", v: "14 / 16" },
];

const TradeGlyph = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8" /></svg>
);
const TrashGlyph = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" /></svg>
);

const TAB_ITEMS = [
  { key: "home", label: "Home", icon: <Icon name="overview" size={22} /> },
  { key: "watch", label: "Watch", icon: <Icon name="watchlist" size={22} /> },
  { key: "trade", label: "Trade", icon: <Icon name="trade" size={22} /> },
  { key: "strategy", label: "Strategy", icon: <Icon name="strategy" size={22} /> },
  { key: "more", label: "More", icon: <Icon name="more" size={22} /> },
];

const SHEET_LISTS = [
  { name: "Nifty 50 core", sub: "12 instruments" },
  { name: "Bank watch", sub: "6 instruments" },
  { name: "Options ladder", sub: "9 instruments" },
];

/*>>> SECTION HELPERS <<<*/
const Section = ({ eyebrow, caption, children }) => (
  <section style={{ marginBottom: 40, minWidth: 0 }}>
    <div className="ds-eyebrow" style={{ marginBottom: 4 }}>{eyebrow}</div>
    {caption && <div className="ds-body-sm" style={{ marginBottom: 14 }}>{caption}</div>}
    {children}
  </section>
);

const Frame = ({ children, style }) => (
  <div
    style={{
      background: "var(--bg-2)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-xl)",
      overflow: "hidden",
      minWidth: 0,
      ...style,
    }}
  >
    {children}
  </div>
);

/*>>> 1. NUMERIC TYPE ROLES <<<*/
const NumericRolesDemo = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
    <div>
      <div className="ds-caption" style={{ marginBottom: 2 }}>.ds-num-hero — 40px</div>
      <div className="ds-num-hero" style={{ color: "var(--fg-1)" }}>₹4,82,190</div>
    </div>
    <div>
      <div className="ds-caption" style={{ marginBottom: 2 }}>.ds-num-xl — 28px</div>
      <div className="ds-num-xl" style={{ color: "var(--fg-1)" }}>₹12,847</div>
    </div>
    <div>
      <div className="ds-caption" style={{ marginBottom: 2 }}>.ds-num-lg — 20px</div>
      <div className="ds-num-lg" style={{ color: "var(--fg-1)" }}>₹8,420</div>
    </div>
  </div>
);

/*>>> 2. NUMBERHERO / STATTILE <<<*/
const HeroStatDemo = () => (
  <div style={{ minWidth: 0 }}>
    <NumberHero
      eyebrow="Today's P&L"
      value="+₹12,847"
      delta={{ text: "+2.34%", sign: 1 }}
      sparkline={SPARK_PROFIT}
    >
      <span style={{ fontSize: 11, color: "var(--fg-3)" }}>vs. yesterday</span>
    </NumberHero>

    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 22 }}>
      {SESSION_STATS.map((s) => (
        <StatTile key={s.k} eyebrow={s.k} value={s.v} size="lg" />
      ))}
    </div>

    <div style={{ marginTop: 22 }}>
      <StatTile
        eyebrow="Day loss example"
        value="−₹3,210"
        size="lg"
        delta={{ text: "−1.18%", sign: -1 }}
        sparkline={SPARK_LOSS}
      />
    </div>
  </div>
);

/*>>> 3. SPARKLINE <<<*/
const SPARK_W = 74;
const SPARK_H = 32;

const SparklineSample = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6, minWidth: 0 }}>
    {children}
    <span className="ds-caption">{label}</span>
  </div>
);

const SparklineDemo = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, minWidth: 0 }}>
      <SparklineSample label="mono">
        <Sparkline data={SPARK_FLAT} width={SPARK_W} height={SPARK_H} tint="mono" />
      </SparklineSample>
      <SparklineSample label="profit">
        <Sparkline data={SPARK_PROFIT} width={SPARK_W} height={SPARK_H} tint="profit" fill />
      </SparklineSample>
      <SparklineSample label="loss">
        <Sparkline data={SPARK_LOSS} width={SPARK_W} height={SPARK_H} tint="loss" fill />
      </SparklineSample>
      <SparklineSample label="auto">
        <Sparkline data={SPARK_PROFIT} width={SPARK_W} height={SPARK_H} tint="auto" fill />
      </SparklineSample>
    </div>
    <SparklineSample label="animate + lastDot">
      <Sparkline data={SPARK_PROFIT} width={160} height={44} tint="profit" fill animate lastDot />
    </SparklineSample>
  </div>
);

/*>>> 4. LISTROW <<<*/
const ChangeTrailing = ({ price, changeText, up }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
    <span className="ds-mono" style={{ color: "var(--fg-1)" }}>{price}</span>
    <span className="ds-mono" style={{ fontSize: 11, color: up ? "var(--color-profit)" : "var(--color-loss)" }}>
      {changeText}
    </span>
  </div>
);

const SymbolTitle = ({ sym, ex }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
    <span style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-1)" }}>{sym}</span>
    <span style={{ fontSize: 9, fontWeight: 600, color: "var(--fg-3)", border: "1px solid var(--border)", padding: "1px 5px", borderRadius: 3, letterSpacing: ".05em", flex: "0 0 auto" }}>
      {ex}
    </span>
  </span>
);

const ListRowDemo = () => (
  <Frame>
    <ListRow
      style={{ borderBottom: "1px solid var(--border)" }}
      leading={
        <span style={{ width: 8, height: 8, borderRadius: 4, background: "var(--color-profit)", boxShadow: "0 0 0 3px rgb(var(--color-profit-rgb) / 0.25)" }} />
      }
      title="Flattrade"
      subtitle="NSE · BSE · F&O"
      trailing={<span style={{ fontSize: 11, color: "var(--fg-2)", fontWeight: 500 }}>Connected</span>}
    />
    <ListRow
      style={{ borderBottom: "1px solid var(--border)" }}
      title={<SymbolTitle sym="RELIANCE" ex="NSE" />}
      subtitle="100 lots · long"
      trailing={<ChangeTrailing price="₹2,847.55" changeText="▲ 61.20 · +2.20%" up />}
    />
    <ListRow
      title={<SymbolTitle sym="INFY" ex="NSE" />}
      subtitle="200 lots · short"
      trailing={<ChangeTrailing price="₹1,492.65" changeText="▼ 9.45 · −0.63%" up={false} />}
    />
  </Frame>
);

/*>>> 5. SWIPEROW — two stacked rows to demo single-open coordination: opening
     one via swipe/drag closes the other (module-level registry in
     SwipeRow.jsx, verified further by code review — see report). <<<*/
const SwipeRowDemo = () => (
  <Frame>
    <SwipeRow
      style={{ borderBottom: "1px solid var(--border)" }}
      actions={[
        { key: "trade", label: "Trade", tone: "accent", icon: TradeGlyph, onClick: () => {} },
        { key: "remove", label: "Remove", tone: "danger", icon: TrashGlyph, onClick: () => {} },
      ]}
    >
      <ListRow
        onClick={() => {}}
        title={<SymbolTitle sym="TATAMOTOR" ex="NSE" />}
        subtitle="80 lots · long"
        trailing={<ChangeTrailing price="₹982.30" changeText="▲ 14.05 · +1.45%" up />}
      />
    </SwipeRow>
    <SwipeRow
      actions={[
        { key: "trade", label: "Trade", tone: "accent", icon: TradeGlyph, onClick: () => {} },
        { key: "remove", label: "Remove", tone: "danger", icon: TrashGlyph, onClick: () => {} },
      ]}
    >
      <ListRow
        onClick={() => {}}
        title={<SymbolTitle sym="HDFCBANK" ex="NSE" />}
        subtitle="50 lots · long"
        trailing={<ChangeTrailing price="₹1,624.10" changeText="▲ 13.80 · +0.86%" up />}
      />
    </SwipeRow>
  </Frame>
);

/*>>> 6. SHEET <<<*/
const SheetDemo = () => {
  const [autoOpen, setAutoOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);

  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      <Button onClick={() => setAutoOpen(true)}>Open sheet</Button>
      <Button variant="secondary" onClick={() => setFullOpen(true)}>Open full cover</Button>

      <Sheet open={autoOpen} onClose={() => setAutoOpen(false)} title="Manage list">
        {SHEET_LISTS.map((l, i) => (
          <ListRow
            key={l.name}
            style={i === SHEET_LISTS.length - 1 ? undefined : { borderBottom: "1px solid var(--border)" }}
            title={l.name}
            subtitle={l.sub}
            trailing={<Icon name="chevron-right" size={16} className="text-fg-3" style={{ color: "var(--fg-3)" }} />}
            onClick={() => {}}
          />
        ))}
      </Sheet>

      <Sheet open={fullOpen} onClose={() => setFullOpen(false)} detent="full">
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px 14px" }}>
            <span className="ds-h2">Add instrument</span>
            <Button iconOnly aria-label="Close" variant="secondary" size="sm" onClick={() => setFullOpen(false)}>
              <Icon name="close" size={16} />
            </Button>
          </div>
          <div style={{ padding: "0 16px" }}>
            <div className="ds-body-sm">Full-cover detent — 100dvh, no grabber/drag (a scrollable list would fight drag-y). Dismiss only via the close button or onClose.</div>
          </div>
        </div>
      </Sheet>
    </div>
  );
};

/*>>> 7. TABBAR — contained in a demo-local phone frame (fixed → absolute
     override lives in a scoped <style> below; the primitive is untouched). <<<*/
const TabBarDemo = () => {
  const [activeKey, setActiveKey] = useState("home");
  return (
    <div className="ds-mobile-demo-tabbar-frame">
      <div className="ds-mobile-demo-tabbar-hint">Tabbed content area</div>
      <TabBar tabs={TAB_ITEMS} activeKey={activeKey} onSelect={setActiveKey} />
    </div>
  );
};

/*>>> 8. MOBILEHEADER <<<*/
const MobileHeaderDemo = () => (
  <Frame>
    <MobileHeader
      title="Dashboard"
      subtitle="Markets open · IST"
      status={{ connected: true }}
      actions={[
        { key: "search", icon: <Icon name="search" size={18} />, onClick: () => {}, "aria-label": "Search" },
        { key: "notify", icon: <Icon name="notify" size={18} />, onClick: () => {}, "aria-label": "Notifications" },
      ]}
      avatar={{ initial: "D", onClick: () => {} }}
    />
  </Frame>
);

/*>>> 9. TOKENS STRIP <<<*/
const TokenRow = ({ name, value, children }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--divider)", minWidth: 0 }}>
    <div style={{ minWidth: 0 }}>
      <div className="ds-mono" style={{ fontSize: 12 }}>{name}</div>
      <div style={{ fontSize: 11, color: "var(--fg-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
    </div>
    <div style={{ flex: "0 0 auto" }}>{children}</div>
  </div>
);

const Swatch = ({ bg, border }) => (
  <div style={{ width: 28, height: 28, borderRadius: 8, background: bg, border: border || "1px solid var(--border)", flex: "0 0 auto" }} />
);

const TokensStripDemo = () => (
  <div style={{ minWidth: 0 }}>
    <TokenRow name="--radius-xl" value="16px · mobile cards">
      <div style={{ width: 40, height: 28, borderRadius: "var(--radius-xl)", background: "var(--bg-3)", border: "1px solid var(--border)" }} />
    </TokenRow>
    <TokenRow name="--radius-sheet" value="28px · bottom-sheet top corners">
      <div style={{ width: 40, height: 24, borderTopLeftRadius: "var(--radius-sheet)", borderTopRightRadius: "var(--radius-sheet)", background: "var(--bg-3)", border: "1px solid var(--border)", borderBottom: "none" }} />
    </TokenRow>
    <TokenRow name="--safe-top / --safe-right / --safe-bottom / --safe-left" value="env(safe-area-inset-*) — notch/home-indicator insets">
      <span className="ds-caption">n/a in browser</span>
    </TokenRow>
    <TokenRow name="--fg-on-accent" value="text painted on an accent fill">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 28, padding: "0 10px", borderRadius: 999, background: "var(--color-accent)", color: "var(--fg-on-accent)", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
        On accent
      </div>
    </TokenRow>
    <TokenRow name="--color-profit" value="teal — signed-positive only">
      <Swatch bg="var(--color-profit)" border="none" />
    </TokenRow>
    <TokenRow name="--color-loss" value="rose — signed-negative only">
      <Swatch bg="var(--color-loss)" border="none" />
    </TokenRow>
  </div>
);

/*>>> 10. ROLLINGNUMBER — a rolling NumberHero + a Randomize button. Each digit
     rolls to its new glyph; ₹ , + − swap instantly; leading digits slide in. <<<*/
const inr = (n) => n.toLocaleString("en-IN");
const RollingNumberDemo = () => {
  const [val, setVal] = useState("+₹12,847");
  const randomize = () => {
    const n = Math.floor(Math.random() * 900000) + 800;
    const up = Math.random() > 0.4;
    setVal(`${up ? "+" : "−"}₹${inr(n)}`);
  };
  return (
    <div style={{ minWidth: 0 }}>
      <NumberHero
        eyebrow="Today's P&L"
        value={val}
        rolling
        delta={{ text: val.startsWith("−") ? "−1.18%" : "+2.34%", sign: val.startsWith("−") ? -1 : 1 }}
      />
      <div style={{ marginTop: 18 }}>
        <Button onClick={randomize}>Randomize</Button>
      </div>
    </div>
  );
};

/*>>> 11. TICKFLASH — an instrument row whose price re-ticks via two buttons.
     TickFlash washes teal/rose behind the block; the price itself is a
     RollingNumber, so flash + roll fire together (the full live-price feel). <<<*/
const fmtRs = (n) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TickFlashDemo = () => {
  const [price, setPrice] = useState(2847.55);
  const [sign, setSign] = useState(1);
  const tick = (dir) => {
    setSign(dir);
    setPrice((p) => Math.max(1, +(p + dir * (Math.random() * 8 + 1)).toFixed(2)));
  };
  const priceStr = fmtRs(price);
  return (
    <div style={{ minWidth: 0 }}>
      <Frame>
        <ListRow
          title={<SymbolTitle sym="RELIANCE" ex="NSE" />}
          subtitle="100 lots · long"
          trailing={
            <TickFlash
              value={priceStr}
              sign={sign}
              style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, borderRadius: 6, padding: "2px 6px", margin: "-2px -6px" }}
            >
              <span className="ds-mono" style={{ color: "var(--fg-1)" }}>
                <RollingNumber value={priceStr} />
              </span>
              <span className="ds-mono" style={{ fontSize: 11, color: sign >= 0 ? "var(--color-profit)" : "var(--color-loss)" }}>
                {sign >= 0 ? "▲" : "▼"} live
              </span>
            </TickFlash>
          }
        />
      </Frame>
      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        <Button onClick={() => tick(1)}>Tick up</Button>
        <Button variant="secondary" onClick={() => tick(-1)}>Tick down</Button>
      </div>
    </div>
  );
};

/*>>> 12. PULLTOREFRESH — a bounded, scrollable phone-frame with a list wrapped
     in PullToRefresh (onRefresh = 1s delay). Drag down at the top to build the
     Loader candlesticks with the pull; past threshold they tint teal (release
     cue), then the candles-only heartbeat loops while the promise pends. <<<*/
const PTR_ROWS = [
  { sym: "RELIANCE", ex: "NSE", price: "₹2,847.55" },
  { sym: "HDFCBANK", ex: "NSE", price: "₹1,624.10" },
  { sym: "INFY", ex: "NSE", price: "₹1,492.65" },
  { sym: "TATAMOTOR", ex: "NSE", price: "₹982.30" },
  { sym: "SBIN", ex: "NSE", price: "₹612.40" },
  { sym: "ICICIBANK", ex: "NSE", price: "₹1,088.75" },
  { sym: "WIPRO", ex: "NSE", price: "₹452.15" },
  { sym: "AXISBANK", ex: "NSE", price: "₹1,142.90" },
];
const PullToRefreshDemo = () => {
  const [count, setCount] = useState(0);
  const refresh = () =>
    new Promise((res) =>
      setTimeout(() => {
        setCount((c) => c + 1);
        res();
      }, 1000)
    );
  return (
    <div style={{ minWidth: 0 }}>
      <div className="ds-mobile-demo-ptr-frame">
        <PullToRefresh onRefresh={refresh}>
          <div style={{ padding: "2px 0 8px" }}>
            <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
              Refreshed {count}×
            </div>
            {PTR_ROWS.map((r, i) => (
              <ListRow
                key={r.sym}
                style={i === PTR_ROWS.length - 1 ? undefined : { borderBottom: "1px solid var(--border)" }}
                title={<SymbolTitle sym={r.sym} ex={r.ex} />}
                trailing={<span className="ds-mono" style={{ color: "var(--fg-1)" }}>{r.price}</span>}
              />
            ))}
          </div>
        </PullToRefresh>
      </div>
      <div className="ds-caption" style={{ marginTop: 8 }}>
        Scroll to the top of the frame, then drag down. On a touch device the candlesticks rise with the pull (high-contrast); past ~72px they tint teal (release cue). Release past threshold and the candles-only heartbeat loops for 1s. (Mouse drag won't engage the touch path.)
      </div>
    </div>
  );
};

/*>>> 13. HAPTICS — Android Chrome only (iOS Safari ignores the Vibration API);
     no-ops under prefers-reduced-motion or when the "ds-haptics" preference is
     off. The Toggle here is the same preference surfaced in MoreSheet, made
     demoable without auth. <<<*/
const HapticsDemo = () => {
  const [on, setOn] = useState(() => hapticsEnabled());
  const toggle = (next) => {
    setHapticsEnabled(next);
    setOn(next);
    if (next) haptics.tap();
  };
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <Button onClick={() => haptics.tap()}>Test haptic</Button>
        <Button variant="secondary" onClick={() => haptics.success()}>Success</Button>
        <Button variant="secondary" onClick={() => haptics.warn()}>Warn</Button>
        <Toggle checked={on} onChange={toggle} label={on ? "On" : "Off"} />
      </div>
      <div className="ds-caption" style={{ marginTop: 10 }}>
        Progressive enhancement — silent on desktop / iOS. Also wired into SwipeRow (row open), TabBar (tab change), and Sheet (drag-dismiss). Default-on; the toggle persists the "ds-haptics" preference per device (localStorage) — flip it off and Test haptic no-ops.
      </div>
    </div>
  );
};

/*>>> PAGE <<<*/
export default function DsMobileShowcase() {
  const [theme, setTheme] = useState("dark");

  return (
    <div data-theme={theme} style={{ minHeight: "100vh", width: "100%", background: "var(--bg-1)", color: "var(--fg-1)" }}>
      {/* Scoped, showcase-only override: TabBar's primitive CSS is
          `position: fixed`; here we contain it inside a demo phone-frame via
          a higher-specificity descendant selector. The primitive file
          (ds-components.css .ds-tabbar) is untouched. */}
      <style>{`
        .ds-mobile-demo-tabbar-frame {
          position: relative;
          height: 220px;
          border-radius: var(--radius-xl);
          background: var(--bg-3);
          border: 1px solid var(--border);
          overflow: hidden;
        }
        .ds-mobile-demo-tabbar-frame .ds-mobile-demo-tabbar-hint {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; color: var(--fg-3); font-family: var(--font-mono);
        }
        .ds-mobile-demo-tabbar-frame .ds-tabbar {
          position: absolute;
        }
        /* Bounded, scrollable frame for the PullToRefresh demo — gives PTR a
           real scroll container to detect scrollTop===0 against. */
        .ds-mobile-demo-ptr-frame {
          position: relative;
          height: 300px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-y: contain;
          border-radius: var(--radius-xl);
          background: var(--bg-2);
          border: 1px solid var(--border);
        }
      `}</style>

      <div style={{ maxWidth: 430, margin: "0 auto", padding: "24px 16px 64px", boxSizing: "border-box", fontFamily: "var(--font-body)" }}>
        <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, letterSpacing: "-0.012em", color: "var(--fg-1)" }}>
          DeeTrade Design System — Mobile
        </h1>
        <p className="ds-body-sm" style={{ margin: "6px 0 18px" }}>
          Phone-first · the mobile primitive layer (Phase 1): Sheet · TabBar · MobileHeader ·
          ListRow · SwipeRow · StatTile/NumberHero · Sparkline. Single column, 430px max,
          verified with no horizontal overflow at 375px wide.
        </p>

        <div style={{ marginBottom: 32 }}>
          <Segmented
            aria-label="Theme"
            value={theme}
            onChange={setTheme}
            options={[
              { value: "light", label: "Light" },
              { value: "dark", label: "Dark" },
            ]}
          />
        </div>

        <Section eyebrow="Numeric type roles" caption="numbers-as-hero · JetBrains Mono · tabular-nums">
          <NumericRolesDemo />
        </Section>

        <Section eyebrow="NumberHero / StatTile" caption="Base figure stays monochrome — signed color lives on the delta chip / sparkline only.">
          <HeroStatDemo />
        </Section>

        <Section eyebrow="RollingNumber" caption="Per-digit odometer roll on value change. Digits roll to their new glyph; ₹ , + − swap instantly; leading digits slide in. tabular-nums, fixed-height cells — no layout jump.">
          <RollingNumberDemo />
        </Section>

        <Section eyebrow="TickFlash" caption="Ambient tick-flash — the background washes teal (up) / rose (down) on every price change and decays over 240ms. Here it wraps a RollingNumber price, so flash + roll fire together.">
          <TickFlashDemo />
        </Section>

        <Section eyebrow="Sparkline" caption="Axis-less trend line, pure SVG. mono inherits currentColor; profit/loss are fixed tints; auto reads the sign of last − first.">
          <SparklineDemo />
        </Section>

        <Section eyebrow="ListRow" caption="Leading / title+subtitle / trailing, 44px min touch target. Broker-connection style + instrument rows with signed change.">
          <ListRowDemo />
        </Section>

        <Section eyebrow="SwipeRow" caption="swipe left to reveal Trade (accent) · Remove (danger)">
          <SwipeRowDemo />
        </Section>

        <Section eyebrow="Sheet" caption="auto detent — grabber + drag-down to dismiss. full detent — 100dvh opaque cover, dismiss via close button only.">
          <SheetDemo />
        </Section>

        <Section eyebrow="PullToRefresh" caption="Native-feeling pull-to-refresh. At scrollTop 0, drag down: the content rubber-bands and the candlesticks rise with your pull; past threshold they tint teal (release cue); then the candles-only heartbeat loops while it refreshes. Never hijacks normal scrolling.">
          <PullToRefreshDemo />
        </Section>

        <Section eyebrow="Haptics" caption="Guarded vibration helper — Android Chrome only, no-ops on desktop / iOS / reduced-motion / when disabled below. Wired into SwipeRow, TabBar, and Sheet drag-dismiss.">
          <HapticsDemo />
        </Section>

        <Section eyebrow="TabBar" caption="Floating glass primary nav. Fixed to the viewport in real use — contained here in a demo phone-frame (wrapper-only CSS override, primitive untouched).">
          <TabBarDemo />
        </Section>

        <Section eyebrow="MobileHeader" caption="Large-title phone header — status dot, subtitle, action cluster, session-ring avatar.">
          <MobileHeaderDemo />
        </Section>

        <Section eyebrow="Tokens" caption="Geometry, safe-area, and signed-color tokens used by the mobile layer.">
          <TokensStripDemo />
        </Section>
      </div>
    </div>
  );
}
